import { useEffect, useState } from "react";
import { AlertTriangle, TrendingDown, Info } from "lucide-react";
import { api } from "@/lib/api";

/**
 * FrictionScoreTranslated — Doctrine 3Q · "Et alors ?"
 *
 * V23.15.32 — Applique la doctrine des 3 questions au score friction :
 *  - Et alors ?     → pertes évitables en CHF/an, calculées via brute × (1-factor)
 *  - Pourquoi ?     → sous-titre "confidence" + fraîcheur de la donnée
 *  - Que fais-je ?  → CTA optionnel vers cartographie friction
 *
 * Le score brut (ex: 0.38) doit TOUJOURS être accompagné de sa traduction
 * financière. Ce composant est le point unique de rendu du score friction.
 *
 * Props :
 *  - factor?       : override du facteur (sinon fetch automatique du GFF)
 *  - size?         : "sm" | "md" | "lg"  (défaut md)
 *  - inline?       : bool — rend en une ligne au lieu d'un bloc
 *  - showCta?      : bool — ajoute le CTA vers /app/gouvernance
 *  - currency?     : "CHF" | "EUR" | "USD"  (défaut CHF)
 */
export default function FrictionScoreTranslated({
  factor: factorOverride,
  size = "md",
  inline = false,
  showCta = false,
  currency = "CHF",
  className = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/workspace/valuations/friction-translation?currency=${currency}`);
        if (cancelled) return;
        setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currency]);

  if (loading) {
    return (
      <div className={`text-xs text-zinc-500 ${className}`} data-testid="friction-score-loading">
        Chargement du score…
      </div>
    );
  }
  if (!data) {
    return (
      <div className={`text-xs text-zinc-500 ${className}`} data-testid="friction-score-empty">
        Score friction indisponible
      </div>
    );
  }

  const factor = typeof factorOverride === "number" ? factorOverride : data.factor;
  const isCritical = factor < 0.5;
  const isWarning = factor >= 0.5 && factor < 0.75;
  const color = isCritical ? "#E24B4A" : isWarning ? "#EF9F27" : "#1D9E75";
  const Icon = isCritical ? AlertTriangle : TrendingDown;

  const scoreLarge = size === "lg" ? "text-3xl" : size === "md" ? "text-2xl" : "text-lg";
  const narrativeSize = size === "lg" ? "text-sm" : "text-xs";

  if (inline) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${className}`}
        title={data.narrative}
        data-testid="friction-score-inline"
      >
        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
        <span className="font-semibold tabular-nums" style={{ color }}>{factor.toFixed(2)}</span>
        <span className="text-zinc-400">·</span>
        <span className="text-zinc-300">{data.pertes_evitables_display}/an évitables</span>
      </span>
    );
  }

  return (
    <div
      className={`rounded-xl border p-3 ${className}`}
      style={{
        background: `linear-gradient(135deg, ${color}0F 0%, transparent 60%)`,
        borderColor: `${color}33`,
      }}
      data-testid="friction-score-translated"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Score Friction
            </span>
            <span className={`font-bold tabular-nums ${scoreLarge}`} style={{ color }}>
              {factor.toFixed(2)}
            </span>
            <span className="text-[10px] text-zinc-500">
              ({data.factor_pct}% de valeur perdue)
            </span>
          </div>
          <div className={`${narrativeSize} text-zinc-200 mt-1 leading-relaxed`} data-testid="friction-score-narrative">
            {data.narrative}
          </div>
          {(data.reductions_count > 0 || data.confidence !== "low") && (
            <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
              <span className="inline-flex items-center gap-1">
                <Info className="w-3 h-3" />
                Confiance : <span className="text-zinc-300 capitalize">{data.confidence}</span>
              </span>
              {data.reductions_count > 0 && (
                <>
                  <span className="opacity-40">·</span>
                  <span>{data.reductions_count} friction(s) détectée(s)</span>
                </>
              )}
            </div>
          )}
          {showCta && data.pertes_evitables_meur > 0 && (
            <a
              href="/app/gouvernance"
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md font-medium transition-colors"
              style={{
                background: `${color}22`,
                border: `1px solid ${color}55`,
                color,
              }}
              data-testid="friction-score-cta"
            >
              Voir les frictions à corriger →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
